import type { MarketplaceAccountStatus, MarketplaceListingView, MarketplaceProviderId } from "@cx/contracts";
import { MARKETPLACE_LABELS, marketplaceIn, marketplaceOrderStatusLabel } from "@cx/domain";
import type { Tone } from "@cx/ui";

/** Rótulos das telas dos marketplaces (Mercado Livre T1.16, Shopee T1.17) e dos canais (T3.3). */

type ProviderUi = {
  label: string;
  /** Nome curto com a preposição ("no ML", "na Shopee") para cabeçalhos de coluna. */
  shortIn: string;
  /** Rota da configuração. */
  settings: string;
  /** O que se conecta: conta de vendedor ou loja. */
  unit: "Conta" | "Loja";
  itemCode: string;
};

/** Textos e caminhos por provedor. */
export const PROVIDER_UI: Record<MarketplaceProviderId, ProviderUi> = {
  mercadolivre: {
    label: MARKETPLACE_LABELS.mercadolivre,
    shortIn: "no ML",
    settings: "/preferencias/integracoes/mercado-livre",
    unit: "Conta",
    itemCode: "MLB",
  },
  shopee: {
    label: MARKETPLACE_LABELS.shopee,
    shortIn: "na Shopee",
    settings: "/preferencias/integracoes/shopee",
    unit: "Loja",
    itemCode: "item_id",
  },
  magalu: {
    label: MARKETPLACE_LABELS.magalu,
    shortIn: "no Magalu",
    settings: "/preferencias/integracoes/canais",
    unit: "Loja",
    itemCode: "SKU",
  },
  amazon: {
    label: MARKETPLACE_LABELS.amazon,
    shortIn: "na Amazon",
    settings: "/preferencias/integracoes/canais",
    unit: "Conta",
    itemCode: "SKU",
  },
  nuvemshop: {
    label: MARKETPLACE_LABELS.nuvemshop,
    shortIn: "na Nuvemshop",
    settings: "/preferencias/integracoes/canais",
    unit: "Loja",
    itemCode: "id do produto",
  },
  shopify: {
    label: MARKETPLACE_LABELS.shopify,
    shortIn: "na Shopify",
    settings: "/preferencias/integracoes/canais",
    unit: "Loja",
    itemCode: "id do produto",
  },
};

export const ACCOUNT_STATUS: Record<MarketplaceAccountStatus, readonly [Tone, string]> = {
  active: ["success", "Conectada"],
  reauth_required: ["warning", "Reconectar"],
  disconnected: ["secondary", "Desconectada"],
};

export const orderStatusLabel = (status: string, provider: MarketplaceProviderId = "mercadolivre") =>
  marketplaceOrderStatusLabel(provider, status);

const review = (p: MarketplaceProviderId): Record<string, string> => ({
  unlinked_items: "há itens sem vínculo com produto: vincule os anúncios e reaplique os vínculos",
  canceled_on_marketplace: `cancelado ${marketplaceIn(p)} (pedido de venda cancelado)`,
  no_items: "pedido sem itens",
  too_many_items: "itens demais para um pedido de venda",
});
const reviewPrefix = (p: MarketplaceProviderId): Record<string, string> => ({
  reserve_failed: "a reserva de estoque falhou",
  import_failed: "o pedido de venda não pôde ser criado",
  cancel_failed: `cancelado ${marketplaceIn(p)}, mas o pedido de venda não pôde ser cancelado`,
});

/** Motivo de revisão do pedido importado em pt-BR; null = sem revisão. */
export function reviewLabel(
  reason: string | null,
  provider: MarketplaceProviderId = "mercadolivre",
): string | null {
  if (!reason) return null;
  const full = review(provider);
  if (Object.hasOwn(full, reason)) return full[reason]!;
  const [prefix, code] = reason.split(":", 2);
  if (prefix === "currency") return `pedido em outra moeda (${code ?? ""}): confira os valores`;
  if (prefix === "status")
    return `situação não tratada ${marketplaceIn(provider)}: ${orderStatusLabel(code ?? "", provider)}`;
  const prefixes = reviewPrefix(provider);
  if (prefix && Object.hasOwn(prefixes, prefix)) return `${prefixes[prefix]} (${code ?? ""})`;
  return reason;
}

/** Situação da sincronização do anúncio. */
export function syncState(l: Pick<MarketplaceListingView, "product" | "syncPending" | "syncError">): {
  tone: Tone;
  label: string;
} {
  if (!l.product) return { tone: "secondary", label: "Sem vínculo" };
  if (l.syncError === "price_conflict") return { tone: "danger", label: "Preços diferentes nas variações" };
  if (l.syncError) return { tone: "danger", label: "Erro" };
  if (l.syncPending) return { tone: "warning", label: "Pendente" };
  return { tone: "success", label: "Em dia" };
}

/** "MLB123 / 1001" quando o anúncio tem variação. */
export const listingRef = (l: Pick<MarketplaceListingView, "externalItemId" | "externalVariationId">) =>
  l.externalVariationId ? `${l.externalItemId} / ${l.externalVariationId}` : l.externalItemId;
